"use client";

import React, { useState } from "react";

type FacultyMember = {
  name: string;
  role: string;
  img: string;
  /** Present only for members whose card should open a video on click. */
  youtubeId?: string;
};

const MAIN_VIDEO_ID = "yBwWdqaQyA8";

const TEAM_FACULTY: FacultyMember[] = [
  {
    name: "Uttam Pal",
    role: "CPA",
    img: "https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-course/why-miles/Uttam_Pal.webp",
  },
  {
    name: "CA Darpan Jain",
    role: "CA, CPA",
    img: "https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-course/why-miles/CA_Darpan-Jain.webp",
  },
  {
    name: "Moosal Kazim Nazeer",
    role: "CPA, CMA, CFA",
    img: "https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-course/why-miles/CMA_Moosal_Kazim_Nazeer.webp",
  },
  {
    name: "Madan M G",
    role: "CPA, CMA",
    img: "https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-course/why-miles/Madan_MG.webp",
  },
  {
    name: "Kamal Rajnikant Vora",
    role: "CPA",
    img: "https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-course/why-miles/Kamal_Rajnikant_Vora.webp",
  },
  {
    name: "Alka Kothari",
    role: "CA, ICWAI, CPA",
    img: "https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-course/why-miles/Alka_Kothari.webp",
  },
];

const GLOBAL_INNOVATORS: FacultyMember[] = [
  {
    name: "Chris Stegh",
    role: "CTO & VP of Strategy, eGroup, Microsoft MVP",
    img: "https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-course/why-miles/Chris_Stegh.webp",
    youtubeId: "zpxNL3UBsFs",
  },
  {
    name: "AJ Ansari",
    role: "COO & Co-Owner, DSWi, Microsoft MVP",
    img: "https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-course/why-miles/AJ_Ansari.webp",
    youtubeId: "3fi2T-idmxc",
  },
  {
    name: "Griffin Lickfeldt",
    role: "Power Platform Consultant - Hitachi Solutions, Microsoft MVP",
    img: "https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-course/why-miles/Griffin_Lickfeldt.webp",
    youtubeId: "KgrAqIQfzAU",
  },
  {
    name: "Jeff Seibert",
    role: "Founder & CEO, Digits",
    img: "https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-course/why-miles/Jeff_Seibert.webp",
    youtubeId: "tKt5Kqxo2VM",
  },
  {
    name: "Leroy Kerry",
    role: "Co-Founder & CEO, Filed",
    img: "https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-course/why-miles/Leroy_Kerry.webp",
    youtubeId: "tivuVuOXmko",
  },
  {
    name: "Michelle Voyer",
    role: "MD, Client Advisory Services, CohnReznick LLP",
    img: "https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-course/why-miles/Michelle_Voyer.webp",
    youtubeId: "hTeo-TTKgjU",
  },
  {
    name: "Gary Boomer",
    role: "Founder & Visionary, Boomer Consulting, Inc",
    img: "https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-course/why-miles/Gary_Boomer.webp",
    youtubeId: "dXx0Z2MkEss",
  },
  {
    name: "Amel Edmond",
    role: "Chief Information Officer, Withum",
    img: "https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-course/why-miles/Amel_Edmond.webp",
    youtubeId: "E2BYNo4wqNI",
  },
  {
    name: "Jordan Fladell",
    role: "Partner & MD, Technology Advisory Services, Aprio LLP",
    img: "https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-course/why-miles/Jordan_Fladell.webp",
    youtubeId: "_-2O1z393RU",
  },
];

const FacultyCard = ({
  member,
  onPlay,
}: {
  member: FacultyMember;
  onPlay?: () => void;
}) => {
  const inner = (
    <>
      {/* eslint-disable-next-line @next/next/no-img-element -- ImageKit URLs
        already carry their transforms, so no loader is needed. */}
      <img
        src={member.img}
        alt={member.name}
        loading="lazy"
        decoding="async"
        className="w-full h-full object-cover block rounded-xl"
      />
      <div
        className="absolute left-[5px] right-[5px] bottom-[5px] p-[5px] text-center"
        style={{
          background: "linear-gradient(0deg, #000 55.88%, rgba(0,0,0,0) 100%)",
        }}
      >
        <p className="m-0 text-white text-xs font-semibold leading-[14px]">
          {member.name}
        </p>
        <p className="m-0 text-white text-[10px] font-normal leading-[1.3]">
          {member.role}
        </p>
      </div>
    </>
  );

  const className =
    "relative flex-none w-[160px] h-[160px] rounded-2xl overflow-hidden bg-[#181818] shadow-[0_0_20px_0_rgba(0,0,0,0.1)] p-[5px]";

  if (onPlay) {
    return (
      <button
        type="button"
        onClick={onPlay}
        aria-label={`Play ${member.name} video`}
        className={`${className} cursor-pointer`}
      >
        {inner}
      </button>
    );
  }

  return <div className={className}>{inner}</div>;
};

const MarqueeRow = ({
  label,
  items,
  animationClassName,
  onCardClick,
}: {
  label: React.ReactNode;
  items: FacultyMember[];
  animationClassName: string;
  onCardClick?: (_youtubeId: string) => void;
}) => {
  const doubled = [...items, ...items];

  return (
    <>
      <p className="flex items-center gap-3 m-0 mb-4 text-white/85 text-[13px] font-semibold tracking-[0.02em] before:content-[''] before:flex-1 before:h-px before:bg-white/25 after:content-[''] after:flex-1 after:h-px after:bg-white/25">
        {label}
      </p>
      <div
        className="group overflow-hidden max-w-[650px]"
        style={{
          WebkitMaskImage:
            "linear-gradient(to right, transparent, #000 6%, #000 94%, transparent)",
          maskImage:
            "linear-gradient(to right, transparent, #000 6%, #000 94%, transparent)",
        }}
      >
        <div
          className={`flex gap-4 w-max group-hover:[animation-play-state:paused] ${animationClassName}`}
        >
          {doubled.map((member, i) => (
            <FacultyCard
              key={`${member.name}-${i}`}
              member={member}
              onPlay={
                onCardClick && member.youtubeId
                  ? () => onCardClick(member.youtubeId as string)
                  : undefined
              }
            />
          ))}
        </div>
      </div>
    </>
  );
};

interface FacultyProps {
  pageName: string;
}

const Faculty: React.FC<FacultyProps> = ({ pageName }) => {
  const [activeYoutubeId, setActiveYoutubeId] = useState<string | null>(null);

  const closeVideo = () => setActiveYoutubeId(null);

  const teamFaculty =
    pageName === "CMA"
      ? TEAM_FACULTY.filter((member) => member.name !== "CA Darpan Jain")
      : TEAM_FACULTY;

  return (
    <>
      <section className="relative bg-cover bg-center bg-no-repeat pt-14 max-md:pt-10 bg-[url('https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-course/why-miles/Leading_CPA_Faculty_BG_desktop.webp')] max-md:bg-[url('https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-course/why-miles/Leading_CPA_Faculty_BG_mob.webp')]">
        <div className="main_container">
          <div className="text-center mb-10 max-md:mb-7">
            <h2 className="m-0 mb-3 text-white text-[32px] max-md:text-xl font-bold leading-[1.3] max-md:leading-tight">
              Learn from India&apos;s{" "}
              <span className="text-[#3399ff]">
                <br className="max-md:inline hidden" />
                Leading {pageName} Faculty
              </span>
            </h2>
            <p className="m-0 text-white text-sm max-md:text-xs font-normal">
              Miles bring together India&apos;s best CPA educators{" "}
              <br className="max-md:inline hidden" />
              and industry experts to guide you at every step
            </p>
          </div>

          <div className="flex max-md:flex-col gap-10 max-md:gap-7 items-start pb-12 max-md:pb-0">
            <div className="flex-[0_1_60%] max-md:flex-1 w-full min-w-0">
              <button
                type="button"
                onClick={() => setActiveYoutubeId(MAIN_VIDEO_ID)}
                aria-label="Play Varun Jain video"
                className="block w-full p-0 border-0 bg-transparent cursor-pointer rounded-2xl overflow-hidden leading-[0]"
              >
                <picture>
                  <source
                    media="(max-width: 768px)"
                    srcSet="https://ik.imagekit.io/mileseducation/miles_website/landing-pages/mob_video_thumbnail.webp"
                  />
                  <source
                    media="(min-width: 769px)"
                    srcSet="https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-course/why-miles/varun_jain_desktop.webp"
                  />
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    src="https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-course/why-miles/varun_jain_desktop.webp"
                    alt="Varun Jain - Learn from World's Favourite CPA Instructor"
                    loading="lazy"
                    className="w-full h-auto block"
                  />
                </picture>
              </button>
            </div>

            <div className="flex-[0_1_40%] max-md:flex-1 w-full min-w-0">
              <MarqueeRow
                label="With a Team of Expert CPA Faculty"
                items={teamFaculty}
                animationClassName="animate-[marquee-backward_22s_linear_infinite]"
              />

              <div className="mt-8 max-md:mt-7">
                <MarqueeRow
                  label="And 100+ Global Accounting Innovators"
                  items={GLOBAL_INNOVATORS}
                  animationClassName="animate-[marquee-forward_32s_linear_infinite]"
                  onCardClick={setActiveYoutubeId}
                />
              </div>
            </div>
          </div>
        </div>

        <p className="hidden max-md:block m-0 bg-[#154c89] px-4 py-3.5 text-center text-white text-sm font-medium">
          🏆 Learn from the faculty trusted by{" "}
          <strong className="text-[#ffba08] font-bold">
            70,000+ learners.
          </strong>
        </p>
      </section>

      {activeYoutubeId && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/75 p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeVideo();
          }}
        >
          <div className="relative w-[900px] max-w-full">
            <button
              type="button"
              onClick={closeVideo}
              aria-label="Close video"
              className="absolute -top-10 right-0 bg-transparent border-0 text-white text-[32px] leading-[1] cursor-pointer"
            >
              &times;
            </button>
            <div className="relative w-full pt-[56.25%] rounded-xl overflow-hidden bg-black">
              <iframe
                src={`https://www.youtube.com/embed/${activeYoutubeId}?autoplay=1&rel=0`}
                title="Faculty video"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                className="absolute inset-0 w-full h-full border-0"
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Faculty;
